'use client';

import OilyticsClient from 'api/OilyticsClient';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { ColorType, createChart, createImageWatermark, IChartApi, LineSeries, LineSeriesPartialOptions, Time } from 'lightweight-charts';
import filter from 'lodash/filter';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import isArray from 'lodash/isArray';
import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import map from 'lodash/map';
import padStart from 'lodash/padStart';
import range from 'lodash/range';
import sortBy from 'lodash/sortBy';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface SeriesPoint {
  date: string;
  value: number;
  trading_days_to_expiry: number;
}

interface DaysToExpirySeries {
  label: string;
  formula: string;
  expiry_date: string;
  increment: number;
  is_current: boolean;
  series: SeriesPoint[];
}

export interface DaysToExpiryChartProps {
  tradeData?: { data: DaysToExpirySeries[] } | null;
  loading?: boolean;
}

// HSL → hex, used below to generate the historic/forward palettes with a
// lightness cap. Standard CSS-Color-4 conversion formula (a/f trick): f(0),
// f(8), f(4) give R, G, B respectively.
function hsl (h: number, s: number, l: number): string {
  const a = s * Math.min(l, 1 - l);
  const f = (n: number): string => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return padStart(Math.round(255 * c).toString(16), 2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

// Historic side follows the same house convention as Seasonality
// (seasonality.tsx / apps/mcp-server/ui/src/seasonality.ts, kept in sync by
// hand): current black, 1-back red, 2-back green, then a muted tail cycling
// through the same 8 hues (blue/orange/purple/teal/pink/brown/neutral/cyan)
// at ~35% saturation, lightness stepping up with age and capped at 0.72 so
// even a vintage well beyond these named stops (via AddSpreadMenu, up to
// MAX_INCREMENT below) stays visibly a color against the chart's white
// background instead of fading toward invisible cream, as the old two-hue
// gradient did for a 2022 vintage next to 2026 ones.
const TAIL_HUES = [207, 33, 261, 174, 316, 19, 0, 187];
function tailColor (i: number): string {
  const hue = TAIL_HUES[i % TAIL_HUES.length];
  const lap = Math.floor(i / TAIL_HUES.length);
  const sat = hue === 0 ? 0.08 : 0.35; // keep the "neutral" slot actually neutral
  const lightness = Math.min(0.72, 0.42 + lap * 0.14);
  return hsl(hue, sat, lightness);
}
function historicColor (i: number): string {
  if (i === 0) return '#D32F2F'; // 1 vintage back — red
  if (i === 1) return '#2E7D32'; // 2 vintages back — green
  return tailColor(i - 2); // 3+ back — muted, stepping lighter with age
}

// Forward vintages (this chart, unlike Seasonality, can have future contracts)
// get a dedicated green ramp that fades — lighter and less saturated — the
// further out it goes, solid color throughout (no alpha) and clamped well
// short of white so even a distant forward vintage stays legible.
const FORWARD_STOPS = 12;
const FORWARD_COLORS = Array.from({ length: FORWARD_STOPS }, (_, i) => {
  const t = i / (FORWARD_STOPS - 1);
  return hsl(140, 0.55 - t * 0.15, 0.30 + t * 0.32); // green, L 30%→62%
});

const CURRENT_COLOR = '#000000';

// Default visible window in trading days (≈ last business quarter).
const DEFAULT_VISIBLE_TDE = 90;

function colorForSeries (s: DaysToExpirySeries): string {
  if (s.increment === 0 || s.is_current) return CURRENT_COLOR;
  if (s.increment < 0) {
    return historicColor(Math.abs(s.increment) - 1);
  }
  const idx = Math.min(FORWARD_COLORS.length - 1, s.increment - 1);
  return FORWARD_COLORS[idx];
}

// ---------- Tenor + anchor helpers ----------
//
// The "Add Spread" menu picks a target vintage from a grid of contract
// periods (months / quarters / years / weeks). Each tenor has its own grid
// shape and its own increment unit on the backend (`roll_formula` in
// packages/oilytics-common/src/oilytics_common/formula.py): a `+1` increment
// shifts by 1 month / 1 quarter / 1 year / 1 week respectively. We classify
// the spread's tenor from its first-leg contract spec — formulas never mix
// tenors — and use that to drive both the grid and the offset math.

type Tenor = 'month' | 'quarter' | 'year' | 'week';

type Anchor =
  | { tenor: 'month'; year: number; month0: number }
  | { tenor: 'quarter'; year: number; quarter: number }
  | { tenor: 'year'; year: number }
  | { tenor: 'week'; year: number; week: number };

const MONTH_CODES = ['F', 'G', 'H', 'J', 'K', 'M', 'N', 'Q', 'U', 'V', 'X', 'Z'];
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];
const MONTH_CODE_TO_IDX: Record<string, number> = {
  F: 0, G: 1, H: 2, J: 3, K: 4, M: 5, N: 6, Q: 7, U: 8, V: 9, X: 10, Z: 11,
};
// Backend clamps |increment| ≤ 72 in watchlists.py — keep us inside that.
const MAX_INCREMENT = 72;

// Parses ONLY historic contract specs (Z25, Q225, CAL25, W0125). Rolling
// specs (M1!, Q1!, CAL1!, W1!) carry no fixed contract date — callers fall
// back to parsing the response `label`, which the backend resolves.
function parseHistoricFirstLeg (text: string): Anchor | null {
  let m: RegExpMatchArray | null;
  if ((m = text.match(/\bCAL(\d{2})\b/))) {
    return { tenor: 'year', year: 2000 + Number(m[1]) };
  }
  if ((m = text.match(/\bQ([1-4])(\d{2})\b/))) {
    return { tenor: 'quarter', year: 2000 + Number(m[2]), quarter: Number(m[1]) - 1 };
  }
  if ((m = text.match(/\bW(\d{2})(\d{2})\b/))) {
    return { tenor: 'week', year: 2000 + Number(m[2]), week: Number(m[1]) };
  }
  if ((m = text.match(/\b([FGHJKMNQUVXZ])(\d{2})\b/))) {
    return { tenor: 'month', year: 2000 + Number(m[2]), month0: MONTH_CODE_TO_IDX[m[1]] };
  }
  return null;
}

function getAnchor (s: { formula: string; label: string } | undefined | null): Anchor | null {
  if (!s) return null;
  return parseHistoricFirstLeg(s.formula) ?? parseHistoricFirstLeg(s.label);
}

function cellToIncrement (anchor: Anchor, cellYear: number, cellPeriod: number): number {
  if (anchor.tenor === 'month') return (cellYear - anchor.year) * 12 + (cellPeriod - anchor.month0);
  if (anchor.tenor === 'quarter') return (cellYear - anchor.year) * 4 + (cellPeriod - anchor.quarter);
  if (anchor.tenor === 'year') return cellYear - anchor.year;
  // Flat ×52 is an approximation (ISO weeks per year vary 52 ↔ 53), but the
  // backend's roll_formula weekly logic wraps modulo 52, so it's consistent.
  return (cellYear - anchor.year) * 52 + (cellPeriod - anchor.week);
}

function cellKey (tenor: Tenor, year: number, period: number): string {
  if (tenor === 'year') return `${year}`;
  if (tenor === 'week') return `${year}-W${period}`;
  return `${year}-${period}`;
}

function anchorToCellKey (a: Anchor): string {
  if (a.tenor === 'month') return cellKey('month', a.year, a.month0);
  if (a.tenor === 'quarter') return cellKey('quarter', a.year, a.quarter);
  if (a.tenor === 'year') return cellKey('year', a.year, 0);
  return cellKey('week', a.year, a.week);
}

// ---------- AddSpreadMenu ----------

interface AddSpreadMenuProps {
  onAdd: (targetIncrement: number) => Promise<void>;
  onToggleHide: (formula: string) => void;
  combinedSeries: DaysToExpirySeries[];
  removedFormulas: Set<string>;
  minIncrement: number;
  maxIncrement: number;
  disabled?: boolean;
}

function AddSpreadMenu ({
  onAdd, onToggleHide, combinedSeries, removedFormulas, minIncrement, maxIncrement, disabled,
}: AddSpreadMenuProps) {
  const [open, setOpen] = useState(false);
  const [yearBack, setYearBack] = useState(1);
  const [yearForward, setYearForward] = useState(1);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const currentSeries = useMemo(
    () => find(combinedSeries, (s) => s.is_current || s.increment === 0),
    [combinedSeries],
  );
  const anchor = useMemo(() => getAnchor(currentSeries ?? null), [currentSeries]);

  // Map each loaded vintage to its grid cell so we can render selected/hidden
  // states and short-circuit the click on already-loaded periods.
  const loadedByCell = useMemo(() => {
    const m = new Map<string, { s: DaysToExpirySeries; isHidden: boolean }>();
    if (!anchor) return m;
    forEach(combinedSeries, (s) => {
      const a = getAnchor(s);
      if (!a || a.tenor !== anchor.tenor) return;
      m.set(anchorToCellKey(a), { s, isHidden: removedFormulas.has(s.formula) });
    });
    return m;
  }, [combinedSeries, removedFormulas, anchor]);

  const handleStepper = async (direction: 'earlier' | 'later') => {
    const targetIncrement = direction === 'earlier' ? minIncrement - 1 : maxIncrement + 1;
    setBusyKey(direction);
    setError(null);
    try {
      await onAdd(targetIncrement);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || `No ${direction} vintage available`);
    } finally {
      setBusyKey(null);
    }
  };

  const handleCell = async (cellYear: number, cellPeriod: number) => {
    if (!anchor) return;
    const inc = cellToIncrement(anchor, cellYear, cellPeriod);
    if (Math.abs(inc) > MAX_INCREMENT) return;
    const key = cellKey(anchor.tenor, cellYear, cellPeriod);
    const loaded = loadedByCell.get(key);
    if (loaded) {
      if (loaded.isHidden) {
        // Re-show via onAdd's "existing" branch (re-uses cached series, no fetch).
        await onAdd(loaded.s.increment);
      } else {
        onToggleHide(loaded.s.formula);
      }
      return;
    }
    setBusyKey(key);
    setError(null);
    try {
      await onAdd(inc);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'No vintage available for that period');
    } finally {
      setBusyKey(null);
    }
  };

  const trigger = (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      className="h-6 text-xs px-2 gap-1"
      title="Add a vintage of this spread"
    >
      Add Spread
      <ChevronDown className="w-3 h-3" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-3">
        {anchor
          ? (
            <GridBody
              anchor={anchor}
              yearBack={yearBack}
              yearForward={yearForward}
              loadedByCell={loadedByCell}
              busyKey={busyKey}
              onExpandBack={() => setYearBack((n) => n + 1)}
              onExpandForward={() => setYearForward((n) => n + 1)}
              onCellClick={handleCell}
            />
          )
          : (
            <div className="flex flex-col gap-1 p-1 text-xs">
              <span className="text-muted-foreground">Couldn&apos;t infer tenor — using stepper.</span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busyKey !== null}
                  onClick={() => handleStepper('earlier')}
                  className="h-6 text-xs px-2 gap-1"
                >
                  <ChevronLeft className="w-3 h-3" />
                  {busyKey === 'earlier' ? 'Loading…' : 'Earlier'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busyKey !== null}
                  onClick={() => handleStepper('later')}
                  className="h-6 text-xs px-2 gap-1"
                >
                  {busyKey === 'later' ? 'Loading…' : 'Later'}
                  <ChevronRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        {error && (
          <div className="mt-2 text-[11px] text-destructive">{error}</div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ---------- GridBody (renders the tenor-specific grid) ----------

interface GridBodyProps {
  anchor: Anchor;
  yearBack: number;
  yearForward: number;
  loadedByCell: Map<string, { s: DaysToExpirySeries; isHidden: boolean }>;
  busyKey: string | null;
  onExpandBack: () => void;
  onExpandForward: () => void;
  onCellClick: (cellYear: number, cellPeriod: number) => void;
}

type CellState = 'unloaded' | 'visible' | 'hidden' | 'busy' | 'outOfRange';

function cellClassName (state: CellState) {
  const base = 'h-7 w-7 text-[11px] rounded border transition-colors font-medium flex items-center justify-center';
  if (state === 'visible') return `${base} border-foreground bg-foreground text-background`;
  if (state === 'hidden') return `${base} border-border text-muted-foreground opacity-60`;
  if (state === 'busy') return `${base} border-border text-muted-foreground opacity-50 cursor-wait`;
  if (state === 'outOfRange') return `${base} border-border text-muted-foreground opacity-30 cursor-not-allowed`;
  return `${base} border-border text-foreground hover:bg-accent`;
}

function GridBody ({
  anchor, yearBack, yearForward, loadedByCell, busyKey, onExpandBack, onExpandForward, onCellClick,
}: GridBodyProps) {
  const years = range(anchor.year - yearBack, anchor.year + yearForward + 1);

  const renderCell = (cellYear: number, cellPeriod: number, label: string, sizeClass?: string) => {
    const inc = cellToIncrement(anchor, cellYear, cellPeriod);
    const outOfRange = Math.abs(inc) > MAX_INCREMENT;
    const key = cellKey(anchor.tenor, cellYear, cellPeriod);
    const loaded = loadedByCell.get(key);
    const busy = busyKey === key;
    let state: CellState = 'unloaded';
    if (outOfRange) state = 'outOfRange';
    else if (busy) state = 'busy';
    else if (loaded && !loaded.isHidden) state = 'visible';
    else if (loaded && loaded.isHidden) state = 'hidden';
    return (
      <button
        key={key}
        type="button"
        disabled={outOfRange || busy}
        onClick={() => onCellClick(cellYear, cellPeriod)}
        className={`${cellClassName(state)}${sizeClass ? ` ${sizeClass}` : ''}`}
      >
        {label}
      </button>
    );
  };

  const yearLabel = (y: number) => `'${String(y).slice(-2)}`;

  const ExpandRow = ({ which }: { which: 'back' | 'forward' }) => (
    <div className="flex justify-center py-0.5">
      <Button
        variant="ghost"
        size="icon"
        className="h-5 w-5"
        onClick={which === 'back' ? onExpandBack : onExpandForward}
        title={which === 'back' ? 'Show earlier year' : 'Show later year'}
      >
        {which === 'back' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </Button>
    </div>
  );

  if (anchor.tenor === 'month') {
    return (
      <div className="flex flex-col gap-1">
        <div className="grid grid-cols-[2.25rem_repeat(12,minmax(0,1.75rem))] gap-1 items-center">
          <div />
          {map(MONTH_LABELS, (l) => (
            <div key={l} className="text-[10px] text-muted-foreground text-center">{l}</div>
          ))}
        </div>
        <ExpandRow which="back" />
        {map(years, (y) => (
          <div key={y} className="grid grid-cols-[2.25rem_repeat(12,minmax(0,1.75rem))] gap-1 items-center">
            <div className="text-[11px] text-muted-foreground pr-1 text-right">{yearLabel(y)}</div>
            {map(MONTH_CODES, (code, idx) => renderCell(y, idx, code))}
          </div>
        ))}
        <ExpandRow which="forward" />
      </div>
    );
  }

  if (anchor.tenor === 'quarter') {
    return (
      <div className="flex flex-col gap-1">
        <div className="grid grid-cols-[2.25rem_repeat(4,minmax(0,2.5rem))] gap-1 items-center">
          <div />
          {map(QUARTER_LABELS, (l) => (
            <div key={l} className="text-[10px] text-muted-foreground text-center">{l}</div>
          ))}
        </div>
        <ExpandRow which="back" />
        {map(years, (y) => (
          <div key={y} className="grid grid-cols-[2.25rem_repeat(4,minmax(0,2.5rem))] gap-1 items-center">
            <div className="text-[11px] text-muted-foreground pr-1 text-right">{yearLabel(y)}</div>
            {map(QUARTER_LABELS, (label, idx) => renderCell(y, idx, label, 'w-10'))}
          </div>
        ))}
        <ExpandRow which="forward" />
      </div>
    );
  }

  if (anchor.tenor === 'year') {
    return (
      <div className="flex flex-col gap-1">
        <ExpandRow which="back" />
        {map(years, (y) => (
          <div key={y} className="flex items-center gap-2">
            <div className="text-[11px] text-muted-foreground pr-1 text-right w-8">{yearLabel(y)}</div>
            {renderCell(y, 0, 'CAL', 'w-12')}
          </div>
        ))}
        <ExpandRow which="forward" />
      </div>
    );
  }

  // weekly: 4 rows × 13 cols per year, 52 cells per year-block
  return (
    <div className="flex flex-col gap-1">
      <ExpandRow which="back" />
      {map(years, (y, yi) => (
        <div
          key={y}
          className={`grid grid-cols-[2.25rem_repeat(13,minmax(0,1.75rem))] gap-1 items-center${yi > 0 ? ' pt-1 border-t border-border/40' : ''}`}
        >
          <div className="row-span-4 self-center text-[11px] text-muted-foreground pr-1 text-right">
            {yearLabel(y)}
          </div>
          {map(range(1, 53), (w) => renderCell(y, w, `W${padStart(String(w), 2, '0')}`, 'h-6 w-7 text-[10px]'))}
        </div>
      ))}
      <ExpandRow which="forward" />
    </div>
  );
}

export default function DaysToExpiryChart ({ tradeData, loading = false }: DaysToExpiryChartProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);

  const [removedFormulas, setRemovedFormulas] = useState<Set<string>>(new Set());
  const [extraSeries, setExtraSeries] = useState<DaysToExpirySeries[]>([]);

  // Reset overlays/removals when the underlying trade changes.
  const baseDataKey = useMemo(
    () => map(tradeData?.data ?? [], (d) => d.formula).join('|'),
    [tradeData],
  );
  useEffect(() => {
    setRemovedFormulas(new Set());
    setExtraSeries([]);
  }, [baseDataKey]);

  const combinedSeries = useMemo(() => {
    const base: DaysToExpirySeries[] = tradeData && isArray(tradeData.data) ? tradeData.data : [];
    const merged = [...base, ...extraSeries];
    // sort historic→current→forward for stable pill order
    return sortBy(merged, (s) => s.increment);
  }, [tradeData, extraSeries]);

  const visibleSeries = useMemo(
    () => filter(combinedSeries, (s) => !removedFormulas.has(s.formula) && isArray(s.series) && s.series.length > 0),
    [combinedSeries, removedFormulas],
  );

  // The "base" formula is whichever loaded series has increment 0 — that's the
  // anchor the backend rolls from. Without it we can't compute new vintages.
  const baseFormula = useMemo(() => {
    const current = find(combinedSeries, (s) => s.is_current || s.increment === 0);
    return current?.formula;
  }, [combinedSeries]);

  const increments = useMemo(() => map(combinedSeries, (s) => s.increment), [combinedSeries]);
  const minIncrement = increments.length > 0 ? Math.min(...increments) : 0;
  const maxIncrement = increments.length > 0 ? Math.max(...increments) : 0;

  const handleAddVintage = async (targetIncrement: number) => {
    if (!baseFormula) {
      throw new Error('No base spread loaded');
    }
    // Skip if we already have it — re-show if previously hidden.
    const existing = find(combinedSeries, { increment: targetIncrement });
    if (existing) {
      setRemovedFormulas((prev) => {
        const next = new Set(prev);
        next.delete(existing.formula);
        return next;
      });
      return;
    }
    const client = await OilyticsClient.create();
    const resp = await client.getTradePreview(baseFormula, 'days_to_expiry', undefined, undefined, undefined, {
      increment: targetIncrement,
    });
    const data = (resp?.data as any)?.data as DaysToExpirySeries[] | undefined;
    if (!data || data.length === 0) {
      throw new Error('No vintage available for that period');
    }
    setExtraSeries((prev) => [...prev, ...data]);
  };

  const handleRemoveSeries = (formula: string) => {
    setRemovedFormulas((prev) => {
      const next = new Set(prev);
      next.add(formula);
      return next;
    });
  };

  // Build the lightweight-charts series. We map each (tde, price) to a Time
  // index by negating TDE so the natural left-to-right axis reads "far from
  // expiry → expiry day" (right). Lightweight Charts requires unique ascending
  // time values per series; the backend already returns one point per date so
  // we dedupe by TDE here.
  useEffect(() => {
    if (!containerRef.current || loading) return undefined;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      rightPriceScale: { visible: true, borderVisible: true },
      leftPriceScale: { visible: false },
      layout: {
        background: { type: ColorType.Solid, color: 'white' },
        textColor: '#222',
      },
      grid: {
        vertLines: { visible: true, color: '#e6e6e6' },
        horzLines: { visible: true, color: '#e6e6e6' },
      },
      crosshair: { mode: 0, vertLine: { labelVisible: false } },
      timeScale: {
        fixLeftEdge: true,
        fixRightEdge: true,
        timeVisible: false,
        tickMarkFormatter: (time: number) => {
          if (!isNumber(time)) return '';
          const tde = Math.abs(time);
          if (tde === 0) return 'EXP';
          return `${tde}d`;
        },
      },
      localization: {
        timeFormatter: (time: number) => {
          if (!isNumber(time)) return '';
          const tde = Math.abs(time);
          if (tde === 0) return 'Expiry';
          return `${tde} TDE`;
        },
      },
    });
    chartRef.current = chart;

    const img = new window.Image();
    img.src = '/assets/hilo-watermark.png';
    img.onload = () => {
      const pane = chart.panes()?.[0];
      if (!pane) return;
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        createImageWatermark(pane, dataUrl, { alpha: 0.05, padding: 20 });
      }
    };

    const commonOpts: LineSeriesPartialOptions = {
      priceLineVisible: false,
      lastValueVisible: false,
      crosshairMarkerVisible: true,
      lineWidth: 1,
    };

    const seriesMap = new Map<string, any>();
    forEach(visibleSeries, (s) => {
      const color = colorForSeries(s);
      const isCurrent = s.is_current || s.increment === 0;
      const line = chart.addSeries(LineSeries, {
        ...commonOpts,
        color,
        lineWidth: isCurrent ? 2 : 1,
      });
      // Dedupe by tde and sort ascending. Use time = -tde so expiry is at the right.
      const byTde = new Map<number, number>();
      forEach(s.series, (p) => {
        if (!isNumber(p.value) || !isFinite(p.value)) return;
        if (!isNumber(p.trading_days_to_expiry)) return;
        // Keep the latest (smallest) tde reading per bucket; if two rows share
        // the same tde the more recent date overwrites the older.
        byTde.set(p.trading_days_to_expiry, p.value);
      });
      const points = sortBy(
        map(Array.from(byTde.entries()), ([tde, value]) => ({ time: -tde as Time, value })),
        (pt) => pt.time as number,
      );
      line.setData(points);
      seriesMap.set(s.formula, line);
    });

    // Default the visible window to the last 252 trading days (≈1 trading
    // year). Users can still zoom out manually to see further-back history.
    if (visibleSeries.length > 0) {
      chart.timeScale().setVisibleRange({
        from: -DEFAULT_VISIBLE_TDE as Time,
        to: 0 as Time,
      });
    }

    const handleCrosshairMove = (param: any) => {
      const tooltip = document.getElementById('dte-tooltip');
      if (!tooltip || !containerRef.current) return;
      if (param.point === undefined || param.point.x < 0 || param.point.y < 0 || param.time === undefined) {
        tooltip.style.display = 'none';
        return;
      }
      const time = param.time as number;
      const tde = Math.abs(time);
      const header = tde === 0 ? 'Expiry day' : `${tde} trading days to expiry`;
      let html = '<div style="background: white; border: 1px solid #ccc; border-radius: 4px; padding: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); font-family: Arial, sans-serif; font-size: 12px;">';
      html += `<div style="font-weight: bold; margin-bottom: 4px;">${header}</div>`;
      forEach(visibleSeries, (s) => {
        const line = seriesMap.get(s.formula);
        if (!line) return;
        const data = param.seriesData.get(line);
        if (!data || !isNumber(data.value)) return;
        const color = colorForSeries(s);
        html += '<div style="display: flex; align-items: center; margin: 2px 0;">';
        html += `<span style="display: inline-block; width: 8px; height: 8px; background-color: ${color}; border-radius: 50%; margin-right: 6px;"></span>`;
        html += `<span>${s.label}: <strong>${data.value.toFixed(2)}</strong></span>`;
        html += '</div>';
      });
      html += '</div>';
      tooltip.innerHTML = html;
      tooltip.style.display = 'block';
      const containerWidth = containerRef.current.clientWidth;
      const cursorX = param.point.x;
      if (cursorX > containerWidth * 0.5) {
        void tooltip.offsetWidth;
        const tooltipWidth = tooltip.getBoundingClientRect().width || 200;
        tooltip.style.left = `${Math.max(10, cursorX - tooltipWidth - 10)}px`;
      } else {
        tooltip.style.left = `${cursorX + 10}px`;
      }
      tooltip.style.top = `${param.point.y - 10}px`;
    };

    chart.subscribeCrosshairMove(handleCrosshairMove);

    const containerEl = containerRef.current;
    const handleMouseLeave = () => {
      const tooltip = document.getElementById('dte-tooltip');
      if (tooltip) tooltip.style.display = 'none';
    };
    containerEl?.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      containerEl?.removeEventListener('mouseleave', handleMouseLeave);
      try {
        chart.unsubscribeCrosshairMove(handleCrosshairMove);
      } catch (err) {
        // ignore — chart may already be disposed
      }
      chart.remove();
    };
  }, [visibleSeries, loading]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full text-gray-400">Loading…</div>
    );
  }

  const hasAnySeries = combinedSeries.length > 0;
  const hasVisibleSeries = visibleSeries.length > 0;

  return (
    <div className="h-full w-full flex flex-col relative">
      {/* Pills + add control: pills wrap inside their own column so the
          Add Spread button stays vertically centered against the pill block
          regardless of how many rows it grows to. */}
      <div className="flex items-center gap-2 py-2 border-b">
        <div className="flex flex-wrap items-center gap-1.5 flex-1 min-w-0">
          {map(combinedSeries, (s) => {
            const isRemoved = removedFormulas.has(s.formula);
            const color = colorForSeries(s);
            return (
              <button
                key={s.formula}
                type="button"
                onClick={() => (isRemoved ? setRemovedFormulas((prev) => {
                  const next = new Set(prev);
                  next.delete(s.formula);
                  return next;
                }) : handleRemoveSeries(s.formula))}
                aria-pressed={!isRemoved}
                aria-label={isRemoved ? `Show ${s.label}` : `Hide ${s.label}`}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs whitespace-nowrap cursor-pointer hover:bg-accent focus:outline-hidden focus-visible:ring-1 focus-visible:ring-ring ${isRemoved ? 'opacity-40' : ''}`}
                style={{ borderColor: color }}
              >
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                <span className="text-[11px]">{s.label}</span>
              </button>
            );
          })}
        </div>
        <div className="shrink-0">
          <AddSpreadMenu
            onAdd={handleAddVintage}
            onToggleHide={handleRemoveSeries}
            combinedSeries={combinedSeries}
            removedFormulas={removedFormulas}
            minIncrement={minIncrement}
            maxIncrement={maxIncrement}
            disabled={!hasAnySeries || !baseFormula}
          />
        </div>
      </div>

      {/* Chart container */}
      <div ref={containerRef} className="flex-grow min-h-0 relative">
        {!hasVisibleSeries && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm pointer-events-none">
            {hasAnySeries ? 'All spreads hidden — re-add from the pills above.' : 'No days-to-expiry data available'}
          </div>
        )}
      </div>

      {/* Floating tooltip */}
      <div
        id="dte-tooltip"
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          display: 'none',
          zIndex: 10,
        }}
      />
    </div>
  );
}
